/*
  Trackpad swipe support for Splide carousels (RY-378).

  Splide only listens for pointer drags, so on desktop a carousel can be moved
  by clicking and dragging but not by swiping two fingers on a trackpad. This
  turns horizontal wheel deltas into carousel movement, mirroring what the
  built-in drag does: follow the swipe 1:1 while the gesture runs, then snap
  to the nearest slide once it stops.

  Vertical deltas are never touched, so the page keeps scrolling as usual.
*/
(function () {
  'use strict';

  var GESTURE_END_DELAY = 120; // quiet time that marks the end of a swipe
  var NEW_GESTURE_GAP = 200; // gap after which we re-decide the gesture axis
  var FADE_STEP_TRAVEL = 60; // px of travel before a fade carousel steps
  var FADE_STEP_COOLDOWN = 400; // ms between steps on a fade carousel
  var LINE_HEIGHT = 16; // px per line for deltaMode: DOM_DELTA_LINE
  var EDGE_EPSILON = 0.5; // px below which a move counts as "no move"
  var BOUND = 'xTrackpadSwipeBound';

  function splideFor(root) {
    var splide = root.splide;

    if (!splide || !window.Splide) return null;

    var states = window.Splide.STATES;
    // Not mounted yet, being dragged by pointer, or torn down at this breakpoint.
    if (splide.state.is([states.CREATED, states.DRAGGING, states.DESTROYED])) return null;

    var components = splide.Components;
    if (!components || !components.Move || !components.Controller || !components.Layout) return null;

    var options = splide.options;
    // The merchant turned swiping off, or the carousel runs top to bottom.
    if (options.drag === false || options.direction === 'ttb') return null;
    if (!components.Layout.isOverflow()) return null;

    return splide;
  }

  function deltas(event, root) {
    var scale = 1;

    if (event.deltaMode === 1) {
      scale = LINE_HEIGHT;
    } else if (event.deltaMode === 2) {
      scale = root.clientWidth || window.innerWidth;
    }

    var x = event.deltaX * scale;
    var y = event.deltaY * scale;

    // Windows and Linux report shift + wheel as a vertical delta.
    if (!x && event.shiftKey) {
      x = y;
      y = 0;
    }

    return { x: x, y: y };
  }

  function snap(splide, state) {
    state.timer = null;

    if (!splideFor(splide.root)) return;
    // Free mode keeps whatever position the swipe landed on.
    if (splide.options.drag === 'free') return;

    var controller = splide.Components.Controller;
    controller.go(controller.toDest(splide.Components.Move.getPosition()), true);
  }

  function scheduleSnap(splide, state) {
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      snap(splide, state);
    }, GESTURE_END_DELAY);
  }

  function scrollTrack(splide, state, deltaX, event) {
    var move = splide.Components.Move;
    var current = move.getPosition();
    // Track positions are plain translateX pixels, so a rightward swipe moves
    // the track left by the same amount.
    var next = current - deltaX;

    if (!splide.is('loop')) {
      var start = move.getLimit(false);
      var end = move.getLimit(true);
      next = Math.min(Math.max(next, Math.min(start, end)), Math.max(start, end));
    }

    // Already at the edge: hand the gesture back to the browser so overscroll
    // and back-swipe still behave natively.
    if (Math.abs(next - current) < EDGE_EPSILON) return;

    event.preventDefault();
    move.translate(next);
    scheduleSnap(splide, state);
  }

  function stepFade(splide, state, deltaX, event) {
    var previous = deltaX < 0;
    if (splide.Components.Controller.getAdjacent(previous) < 0) return;

    event.preventDefault();

    var now = event.timeStamp || Date.now();
    if (now - state.steppedAt < FADE_STEP_COOLDOWN) return;

    state.travel += deltaX;
    if (Math.abs(state.travel) < FADE_STEP_TRAVEL) return;

    splide.go(previous ? '<' : '>');
    state.travel = 0;
    state.steppedAt = now;
  }

  function onWheel(root, state, event) {
    // A nested carousel already took this swipe.
    if (event.defaultPrevented) return;

    var splide = splideFor(root);
    if (!splide) return;

    var now = event.timeStamp || Date.now();
    if (now - state.lastAt > NEW_GESTURE_GAP) {
      state.axis = null;
      state.travel = 0;
    }
    state.lastAt = now;

    var delta = deltas(event, root);

    // Lock the axis on the first move of a gesture so a mostly-vertical scroll
    // is never stolen half way through.
    if (!state.axis) {
      if (!delta.x && !delta.y) return;
      state.axis = Math.abs(delta.x) > Math.abs(delta.y) ? 'x' : 'y';

      if (state.axis === 'x') {
        splide.Components.Move.cancel();
        if (splide.Components.Scroll) splide.Components.Scroll.cancel();
      }
    }

    if (state.axis !== 'x' || !delta.x) return;

    if (splide.is('fade')) {
      stepFade(splide, state, delta.x, event);
    } else {
      scrollTrack(splide, state, delta.x, event);
    }
  }

  function bind(root) {
    if (root[BOUND]) return;
    root[BOUND] = true;

    var state = { axis: null, lastAt: 0, travel: 0, steppedAt: 0, timer: null };

    root.addEventListener(
      'wheel',
      function (event) {
        onWheel(root, state, event);
      },
      { passive: false }
    );
  }

  // Carousels mount lazily as they scroll into view, so bind on hover rather
  // than up front. That also keeps the non-passive wheel listener on the few
  // elements that need it instead of on the document.
  document.addEventListener(
    'pointerover',
    function (event) {
      var node = event.target;
      if (!node || !node.closest) return;

      for (var root = node.closest('.splide'); root; root = root.parentElement && root.parentElement.closest('.splide')) {
        bind(root);
      }
    },
    { passive: true, capture: true }
  );
})();
